import React from 'react';
import styled from 'styled-components';

// Import des icônes SVG
import computerIcon from '../../assets/icons/computer.svg';
import codeIcon from '../../assets/icons/code.svg';
import reactIcon from '../../assets/icons/react.svg';
import typescriptIcon from '../../assets/icons/typescript.svg';
import nodejsIcon from '../../assets/icons/nodejs.svg';

import chefHatIcon from '../../assets/icons/chef-hat.svg';
import cookingPotIcon from '../../assets/icons/cooking-pot.svg';
import recipeBookIcon from '../../assets/icons/recipe-book.svg';
import spatulaIcon from '../../assets/icons/spatula.svg';
import ovenIcon from '../../assets/icons/oven.svg';

import settingsIcon from '../../assets/icons/settings.svg';
import wrenchIcon from '../../assets/icons/wrench.svg';
import hammerIcon from '../../assets/icons/hammer.svg';
import screwdriverIcon from '../../assets/icons/screwdriver.svg';

import bookIcon from '../../assets/icons/book.svg';
import bookmarkIcon from '../../assets/icons/bookmark.svg';
import linkIcon from '../../assets/icons/link.svg';
import folderIcon from '../../assets/icons/folder.svg';
import starIcon from '../../assets/icons/star.svg';

import mailIcon from '../../assets/icons/mail.svg';
import phoneIcon from '../../assets/icons/phone.svg';
import messageIcon from '../../assets/icons/message.svg';
import locationIcon from '../../assets/icons/location.svg';
import calendarIcon from '../../assets/icons/calendar.svg';

// Configuration des icônes par section
const iconSets = {
  about: [
    computerIcon,
    codeIcon,
    reactIcon,
    typescriptIcon,
    nodejsIcon,
  ],
  projects: [
    chefHatIcon,
    cookingPotIcon,
    recipeBookIcon,
    spatulaIcon,
    ovenIcon,
  ],
  tools: [
    settingsIcon,
    wrenchIcon,
    hammerIcon,
    screwdriverIcon,
  ],
  resources: [
    bookIcon,
    bookmarkIcon,
    linkIcon,
    folderIcon,
    starIcon,
  ],
  contact: [
    mailIcon,
    phoneIcon,
    messageIcon,
    locationIcon,
    calendarIcon,
  ],
};

const IconPatternContainer = styled.div<{ $sectionId: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.2;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
`;

const IconRain = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const IconItem = styled.div<{ 
  $delay: number; 
  $rotation: number; 
  $left: number; 
  $size: number;
  $duration: number;
}>`
  position: absolute;
  left: ${props => props.$left}%;
  top: -50px;
  animation: rain ${props => props.$duration}s linear infinite;
  animation-delay: ${props => props.$delay}s;
  transform: rotate(${props => props.$rotation}deg);
  
  img {
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    opacity: 0.4;
    filter: grayscale(100%) brightness(0.7);
    transition: all 0.3s ease;
  }
  
  &:hover img {
    opacity: 0.7;
    filter: grayscale(0%) brightness(1);
  }

  @keyframes rain {
    0% {
      transform: translateY(-100px) rotate(${props => props.$rotation}deg);
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      transform: translateY(calc(100vh + 100px)) rotate(${props => props.$rotation + 360}deg);
      opacity: 0;
    }
  }
`;

interface IconPatternProps {
  sectionId: string;
}

export const IconPattern: React.FC<IconPatternProps> = ({ sectionId }) => {
  const icons = iconSets[sectionId as keyof typeof iconSets] || [];
  
  // Descriptions d'alt text pour chaque section
  const altTexts = {
    about: 'Technology icon',
    projects: 'Cooking icon',
    tools: 'Tool icon',
    resources: 'Resource icon',
    contact: 'Contact icon'
  };
  
  // Créer beaucoup plus d'icônes pour l'effet pluie
  const rainIcons = [];
  const iconCount = 15; // Nombre d'icônes réduit pour les performances
  
  for (let i = 0; i < iconCount; i++) {
    const randomIcon = icons[Math.floor(Math.random() * icons.length)];
    const randomLeft = Math.random() * 100;
    const randomDelay = Math.random() * 3; // Réduit pour les performances
    const randomRotation = Math.random() * 45; // Réduit pour les performances
    const randomSize = 16 + Math.random() * 12; // Entre 16px et 28px (réduit)
    const randomDuration = 6 + Math.random() * 6; // Entre 6s et 12s (réduit)
    
    rainIcons.push({
      icon: randomIcon,
      left: randomLeft,
      delay: randomDelay,
      rotation: randomRotation,
      size: randomSize,
      duration: randomDuration,
      key: i,
      altText: altTexts[sectionId as keyof typeof altTexts] || 'Decorative icon'
    });
  }
  
  return (
    <IconPatternContainer $sectionId={sectionId}>
      <IconRain>
        {rainIcons.map((rainIcon) => (
          <IconItem
            key={rainIcon.key}
            $delay={rainIcon.delay}
            $rotation={rainIcon.rotation}
            $left={rainIcon.left}
            $size={rainIcon.size}
            $duration={rainIcon.duration}
          >
            <img src={rainIcon.icon} alt={rainIcon.altText} />
          </IconItem>
        ))}
      </IconRain>
    </IconPatternContainer>
  );
};
